import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { EndOrderDetailPaymentStatus } from '@pedix-workspace/utils';
import { UNPAID_PAYMENT_STATUS } from '@pedix-workspace/orders';
import { TagComponent, TagType } from '@pedix-workspace/angular-ui-alert';
import { TranslocoService } from '@jsverse/transloco';
import {
  IconBanComponent,
  IconCashRegisterComponent,
  IconCheckComponent,
  IconClockComponent,
  IconReturnComponent,
} from '@pedix-workspace/angular-ui-icons';

type TagIcon = 'clock' | 'check' | 'return' | 'ban' | 'cash-register';
type StatusConfig = { tagType: TagType; icon: TagIcon; labelKey: string };

export type PaymentTagStatus = EndOrderDetailPaymentStatus | typeof UNPAID_PAYMENT_STATUS;

const STATUS_CONFIG: Record<PaymentTagStatus, StatusConfig> = {
  UNPAID: { tagType: 'warning', icon: 'clock', labelKey: 'paymentStatus.UNPAID' },
  PENDING: { tagType: 'info', icon: 'clock', labelKey: 'paymentStatus.PENDING' },
  PARTIAL: { tagType: 'warning', icon: 'clock', labelKey: 'paymentStatus.PARTIAL' },
  PAID: { tagType: 'success', icon: 'check', labelKey: 'paymentStatus.PAID' },
  REFUNDED: { tagType: 'info', icon: 'return', labelKey: 'paymentStatus.REFUNDED' },
  REJECTED: { tagType: 'danger', icon: 'ban', labelKey: 'paymentStatus.REJECTED' },
  CANCELLED: { tagType: 'danger', icon: 'ban', labelKey: 'paymentStatus.CANCELLED' },
};

@Component({
  selector: 'pxw-payment-status-tag',
  template: `
    <pxw-tag [type]="config().tagType" size="xs">
      @switch (icon()) {
        @case ('clock') { <pxw-icon icon="clock" size="xs" /> }
        @case ('check') { <pxw-icon icon="check" size="xs" /> }
        @case ('return') { <pxw-icon icon="return" size="xs" /> }
        @case ('ban') { <pxw-icon icon="ban" size="xs" /> }
        @case ('cash-register') { <pxw-icon icon="cash-register" size="xs" /> }
      }
      {{ label() }}
    </pxw-tag>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TagComponent,
    IconCashRegisterComponent,
    IconClockComponent,
    IconCheckComponent,
    IconBanComponent,
    IconReturnComponent,
  ],
})
export class PaymentStatusTagComponent {
  private readonly t = inject(TranslocoService);

  readonly status = input.required<PaymentTagStatus>();


  readonly config = computed((): StatusConfig => {
    return STATUS_CONFIG[this.status()] ?? STATUS_CONFIG.PENDING;
  });

  // t(paymentStatus.UNPAID,paymentStatus.PAID,paymentStatus.PENDING,paymentStatus.PARTIAL,paymentStatus.CANCELLED,paymentStatus.REFUNDED,paymentStatus.REJECTED)
  readonly label = computed((): string => {
    return this.t.translate(this.config().labelKey);
  });

  readonly icon = computed((): TagIcon => this.config().icon);
}
